import React from 'react';
import Header from '../Components/Header';
import MainContainer from '../Components/MainContainer';
import ErrorModal from '../Components/Modal/ErrorModal';
import Modal from '../Components/Modal/Modal';
import TextInput from '../Components/TextInput';
import { ApiInstance, ApiRoutes } from '../Utils/ApiInstance';

function getQrPrefillInfo() {
  const query = new URLSearchParams(window.location.search);
  const receiptNo = query.get('receipt_no');
  const token = query.get('token');

  if (receiptNo && token) {
    return {
      isQrMode: true,
      receiptNo,
      token,
    };
  }

  return {
    isQrMode: false,
    receiptNo: '',
    token: '',
  };
}

function ReceiptValidation({ onValidationSuccess }) {
  const qrData = React.useMemo(() => getQrPrefillInfo(), []);
  const [receiptNo, setReceiptNo] = React.useState(qrData.receiptNo);
  const [isValidating, setIsValidating] = React.useState(false);
  const [validationStatus, setValidationStatus] = React.useState(null); // null, 'valid', 'invalid'
  const [showModal, setShowModal] = React.useState(false);
  const [modalCode, setModalCode] = React.useState(1);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [errReceiptNo, setErrReceiptNo] = React.useState('');
  const [isExpired, setIsExpired] = React.useState(false);
  const [expiredInfo, setExpiredInfo] = React.useState({
    purchaseDate: '',
    hoursElapsed: 0,
    errorCode: '',
  });

  const resetValidation = () => {
    setReceiptNo('');
    setValidationStatus(null);
    setErrorMessage('');
    setErrReceiptNo('');
    setIsExpired(false);
    setExpiredInfo({
      purchaseDate: '',
      hoursElapsed: 0,
      errorCode: '',
    });
  };

  const retryValidation = () => {
    // Clear error state and immediately retry validation with updated receipt number
    setValidationStatus(null);
    setErrorMessage('');
    setErrReceiptNo('');
    setIsExpired(false);
    setExpiredInfo({
      purchaseDate: '',
      hoursElapsed: 0,
      errorCode: '',
    });
    // Immediately trigger validation with the updated receipt number
    validateReceipt();
  };

  const validateReceipt = async () => {
    if (isValidating) return;

    // Clear previous errors
    setErrReceiptNo('');
    setValidationStatus(null);

    // Basic validation
    if (!receiptNo.trim()) {
      setErrReceiptNo('Receipt number is required');
      return;
    }

    setIsValidating(true);

    try {
      const body = {
        receipt_no: receiptNo,
        ...(qrData.isQrMode && qrData.token && { token: qrData.token }),
      };

      const response = await ApiInstance.post(ApiRoutes.VALIDATE_RECEIPT, body);

      // Strict validation: only proceed if valid is explicitly true
      if (response.data && response.data.valid === true) {
        setValidationStatus('valid');
        // Proceed to next step immediately for smoother UX
        onValidationSuccess({
          receiptNo,
          isQrMode: qrData.isQrMode,
          token: qrData.token,
        });
      } else {
        setValidationStatus('invalid');
        const errorMsg =
          response.data?.message || response.data?.error_code || 'Receipt not found or invalid';
        setErrorMessage(errorMsg);

        // Check for specific error codes
        if (response.data?.error_code === 'RECEIPT_EXPIRED') {
          setIsExpired(true);
          setExpiredInfo({
            purchaseDate: response.data.purchase_date || '',
            hoursElapsed: response.data.hours_elapsed || 0,
            errorCode: response.data.error_code || 'RECEIPT_EXPIRED',
          });
        }
      }
    } catch (error) {
      setValidationStatus('invalid');

      if (error.response?.status === 422) {
        const errorData = error.response.data;
        if (errorData.errors) {
          // Handle validation errors
          const errorMessages = Object.values(errorData.errors).flat();
          setErrorMessage(`Validation Error: ${errorMessages.join(', ')}`);
        } else {
          setErrorMessage(errorData.message || 'Invalid request format');
        }
      } else if (error.response?.data?.error_code === 'RECEIPT_EXPIRED') {
        // Handle expired receipts
        setIsExpired(true);
        setExpiredInfo({
          purchaseDate: error.response.data.purchase_date || '',
          hoursElapsed: error.response.data.hours_elapsed || 0,
          errorCode: error.response.data.error_code || 'RECEIPT_EXPIRED',
        });
        setErrorMessage(error.response.data.message || 'E-Invoice request window has expired.');
      } else {
        setErrorMessage(
          error.response?.data?.message || 'Unable to validate receipt. Please try again.'
        );
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleReceiptNoChange = e => {
    setReceiptNo(e.target.value);
  };

  // Manual override removed per requirement: user must validate successfully.

  return (
    <MainContainer title={'Receipt Validation'}>
      <div className="x-master">
        <div className="x-master-wrapper">
          <Header />
          <main className="x-main for-subpage">
            <div className="x-settings-wrapper">
              <div className="x-settings">
                <div className="section-form">
                  <div className="container-form-main">
                    <div className="form-column">
                      <div className="form-header-text">Step 1 of 2: Verify Receipt Details</div>
                      <div className="form-subtitle">
                        Please enter your receipt number to verify your purchase.
                      </div>

                      <div className="form-row enhanced-gap">
                        <div className="form-input receipt-input-wrapper">
                          <label className="receipt-label" htmlFor="receipt-number-field">
                            Receipt No.<span className="text-required">*</span>
                          </label>
                          <div className="receipt-input-line">
                            <TextInput
                              id="receipt-number-field"
                              label={undefined}
                              placeholder="Enter receipt number"
                              value={receiptNo}
                              onChange={handleReceiptNoChange}
                              error={errReceiptNo}
                              disabled={qrData.isQrMode}
                              aria-label="Receipt number"
                            />
                            <button
                              type="button"
                              className="vr-btn"
                              onClick={
                                validationStatus === 'invalid' || isExpired
                                  ? retryValidation
                                  : validateReceipt
                              }
                              disabled={isValidating || validationStatus === 'valid'}
                              aria-label={
                                validationStatus === 'invalid' || isExpired
                                  ? 'Try again with the current receipt number'
                                  : 'Validate the entered receipt number'
                              }
                            >
                              {isValidating
                                ? 'Validating...'
                                : validationStatus === 'invalid' || isExpired
                                  ? 'Try Again'
                                  : 'Validate'}
                            </button>
                          </div>
                          {errReceiptNo && (
                            <div className="inline-error" role="alert">
                              {errReceiptNo}
                            </div>
                          )}
                        </div>
                      </div>

                      {validationStatus && !isExpired && (
                        <div className={`validation-status ${validationStatus}`}>
                          {validationStatus === 'valid' ? (
                            <div className="success-message">✅ Receipt verified successfully!</div>
                          ) : (
                            <div className="error-message">❌ {errorMessage}</div>
                          )}
                        </div>
                      )}

                      {isExpired && (
                        <div className="expired-panel" role="alert" aria-live="assertive">
                          <div className="expired-icon">⏰</div>
                          <div className="expired-body">
                            <div className="expired-header-row">
                              <h4>E-Invoice Window Expired</h4>
                              {expiredInfo.errorCode && (
                                <span className="expired-badge" title="Backend error code">
                                  {expiredInfo.errorCode}
                                </span>
                              )}
                            </div>
                            <p className="expired-msg">{errorMessage}</p>
                            <div className="expired-keyfacts">
                              {expiredInfo.purchaseDate && (
                                <div className="fact">
                                  <label>Purchase Date</label>
                                  <div className="value">{expiredInfo.purchaseDate}</div>
                                </div>
                              )}
                              <div className="fact">
                                <label>Allowed Window</label>
                                <div className="value">72 hours</div>
                              </div>
                              {expiredInfo.hoursElapsed ? (
                                <div className="fact">
                                  <label>Days Ago</label>
                                  <div className="value">
                                    {Math.ceil(expiredInfo.hoursElapsed / 24)}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                            <p className="expired-next">
                              This receipt is outside the permitted request period. If you believe
                              this is in error or require documentation, email{' '}
                              <a
                                href="mailto:customer-service@dobiqueen.my"
                                className="support-link"
                              >
                                customer-service@dobiqueen.my
                              </a>{' '}
                              with:
                              <br />• A clear photo of the full receipt (top to bottom)
                              <br />• The purpose you need the e-invoice for
                              <br />• (Optional) WhatsApp{' '}
                              <a
                                className="whatsapp-link wa-number"
                                href="https://wa.me/60102533898?text=Hi%20Dobiqueen%20Support%2C%20my%20receipt%20number%20_____%20has%20expired.%20Please%20advise."
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open WhatsApp chat in new tab"
                              >
                                +6010-253 3898
                              </a>
                              <br />
                            </p>
                            <button
                              type="button"
                              className="try-again-btn"
                              onClick={resetValidation}
                            >
                              Try Different Receipt Number
                            </button>
                          </div>
                        </div>
                      )}

                      {validationStatus === 'invalid' && !isExpired && (
                        <div className="support-help compact" role="note" aria-live="polite">
                          <p className="support-title">Validation failed – check & try again</p>
                          <p className="support-hint">
                            Ensure there are no spaces, missing characters, or letter/number
                            mix-ups. Re-type the code exactly as printed.
                          </p>
                          <button type="button" className="try-again-btn" onClick={resetValidation}>
                            Try Different Receipt Number
                          </button>
                        </div>
                      )}

                      <div className="tutorial-panel" aria-labelledby="receipt-tutorial-heading">
                        <div className="tutorial-visual">
                          <img
                            src={process.env.PUBLIC_URL + '/images/receipt_no_guide.png'}
                            alt="Guide showing highlighted receipt number location on sample receipt"
                            className="tutorial-image"
                          />
                        </div>
                        <div className="tutorial-text">
                          <h3 id="receipt-tutorial-heading">Where to find your receipt number</h3>
                          <ol className="tutorial-steps">
                            <li>
                              Locate the printed or emailed receipt you received after purchase.
                            </li>
                            <li>
                              Look near the top-right corner for your <strong>Receipt No</strong>.
                            </li>
                            <li>Enter the full code exactly as shown, without spaces.</li>
                            <li>
                              Press the <strong>Validate</strong> button to verify and continue.
                            </li>
                          </ol>
                          <p className="tutorial-note">
                            Need help?{' '}
                            <a href="mailto:customer-service@dobiqueen.my" className="support-link">
                              customer-service@dobiqueen.my
                            </a>{' '}
                            or WhatsApp{' '}
                            <a
                              className="whatsapp-link wa-number"
                              href="https://wa.me/60102533898?text=Hi%20Dobiqueen%20Support%2C%20I%20need%20help%20locating%20my%20receipt%20number."
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              +6010-253 3898
                            </a>
                          </p>
                        </div>
                      </div>

                      {qrData.isQrMode && (
                        <div className="qr-mode-notice">
                          <p>
                            🔗 QR Code Mode: Receipt details are pre-filled and cannot be modified.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {showModal && (
        <>
          {modalCode === 1 && <Modal onClose={() => setShowModal(false)} />}
          {modalCode === 2 && (
            <ErrorModal onClose={() => setShowModal(false)} message={errorMessage} />
          )}
        </>
      )}

      <style>{`
        .validation-status {
          margin: 15px 0;
          padding: 12px;
          border-radius: 6px;
          font-weight: 500;
        }
        .expired-panel {
          display: flex;
          gap: 16px;
          background: #fff7ed;
          border: 1px solid #fdba74;
          border-left: 5px solid #f97316;
          padding: 18px 20px;
          border-radius: 12px;
          margin-top: 22px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }
        .expired-icon {
          font-size: 32px;
          line-height: 1;
        }
        .expired-body h4 {
          margin: 0 0 6px;
          font-size: 16px;
          font-weight: 600;
          color: #9a3412;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .expired-badge {
          background: #9a3412;
          color: #fff;
          font-size: 11px;
          letter-spacing: 0.5px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 9999px;
          text-transform: uppercase;
        }
        .expired-msg {
          margin: 0 0 10px;
          color: #7c2d12;
          font-size: 13.5px;
          line-height: 1.4;
        }
        .expired-keyfacts {
          display: flex;
          flex-wrap: wrap;
          gap: 14px 24px;
          margin: 0 0 14px;
        }
        .expired-keyfacts .fact {
          min-width: 140px;
        }
        .expired-keyfacts .fact label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #9a3412;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 2px;
        }
        .expired-keyfacts .fact .value {
          font-size: 13px;
          font-weight: 500;
          color: #7c2d12;
        }
        .expired-next {
          margin: 0;
          font-size: 12.5px;
          line-height: 1.5;
          color: #7c2d12;
        }
        .enhanced-gap {
          margin-top: 10px;
        }
        .receipt-input-wrapper {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .receipt-label {
          font-weight: 600;
          font-size: 14px;
          color: #374151;
        }
        .text-required {
          color: #dc2626;
          margin-left: 2px;
        }
        .receipt-input-line {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        .receipt-input-line input {
          flex: 1;
        }
        .vr-btn {
          background: linear-gradient(90deg, #2563eb, #1d4ed8);
          color: #fff;
          font-weight: 600;
          padding: 10px 22px;
          border: none;
          cursor: pointer;
          border-radius: 9999px;
          font-size: 14px;
          line-height: 1;
          position: relative;
          transition: all 0.18s ease;
          box-shadow: 0 4px 10px -2px rgba(37, 99, 235, 0.35);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 100px;
          height: 40px;
        }
        .vr-btn:hover:not(:disabled) {
          background: linear-gradient(90deg, #1d4ed8, #1e40af);
          box-shadow: 0 6px 14px -2px rgba(30, 64, 175, 0.45);
          transform: translateY(-1px);
        }
        .vr-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 3px 8px -2px rgba(30, 64, 175, 0.4);
        }
        .vr-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .vr-btn:focus-visible {
          outline: 2px solid #1d4ed8;
          outline-offset: 3px;
        }
        .inline-error {
          font-size: 12px;
          color: #b91c1c;
          margin-top: 2px;
        }
        .support-help {
          margin-top: 18px;
          background: #fff;
          border: 1px solid #f5c2c7;
          border-left: 4px solid #dc2626;
          padding: 14px 16px 16px;
          border-radius: 10px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
        }
        .support-title {
          margin: 0 0 4px;
          font-weight: 600;
          font-size: 14px;
          color: #b91c1c;
        }
        .support-text {
          margin: 0 0 8px;
          font-size: 13px;
          line-height: 1.4;
          color: #374151;
        }
        .support-link {
          color: #1d4ed8;
          text-decoration: underline;
        }
        .support-link:hover {
          color: #1e40af;
        }
        .whatsapp-link {
          display: inline-block;
          margin-top: 4px;
          color: #047857;
          font-weight: 600;
          text-decoration: underline;
        }
        .whatsapp-link:hover {
          color: #065f46;
        }
        .wa-number {
          font-weight: 600;
          color: #047857;
        }
        .support-list {
          margin: 0;
          padding-left: 18px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 12.5px;
          color: #555;
        }
        .tutorial-panel {
          margin-top: 40px;
          display: grid;
          gap: 28px;
          grid-template-columns: 160px 1fr;
          align-items: start;
          background: #f9fafb;
          padding: 20px 24px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
        }
        @media (max-width: 700px) {
          .tutorial-panel {
            grid-template-columns: 1fr;
          }
        }
        .tutorial-visual {
          width: 100%;
        }
        .tutorial-image {
          width: 100%;
          border-radius: 8px;
          object-fit: cover;
          border: 1px solid #e5e7eb;
        }
        .tutorial-text h3 {
          margin: 0 0 8px;
          font-size: 16px;
          font-weight: 600;
          color: #111827;
        }
        .tutorial-steps {
          margin: 0 0 10px 20px;
          padding: 0;
          font-size: 14px;
          color: #374151;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .tutorial-steps code {
          background: #1f2937;
          color: #f9fafb;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 12px;
        }
        .tutorial-note {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
        }

        .validation-status.valid {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .validation-status.invalid {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .success-message,
        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .button-secondary {
          background-color: #6c757d;
          color: white;
          border: 1px solid #6c757d;
        }

        .button-secondary:hover {
          background-color: #5a6268;
          border-color: #545b62;
        }

        .qr-mode-notice {
          background-color: #e3f2fd;
          color: #1565c0;
          padding: 10px;
          border-radius: 6px;
          margin-top: 15px;
          border-left: 4px solid #2196f3;
        }

        .form-subtitle {
          color: #6c757d;
          margin-bottom: 25px;
          font-size: 14px;
        }

        .try-again-btn {
          background-color: #dc3545;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          margin-top: 12px;
          transition: background-color 0.2s ease;
        }

        .try-again-btn:hover {
          background-color: #c82333;
        }

        .try-again-btn:focus {
          outline: 2px solid #dc3545;
          outline-offset: 2px;
        }
      `}</style>
    </MainContainer>
  );
}

export default ReceiptValidation;
